<template>
  <DialogContainer
    title="Add Activity"
    icon="add"
    :showDialog="showDialog"
    :toolbarActions="[
      {
        text: 'Save',
        icon: 'save',
        buttonColor: 'success',
        emitEvent: 'onSubmitClicked',
        showIsLoading: true,
        isDisabledFunc: () => !isFormValid,
      },
      {
        text: 'Cancel',
        depressed: true,
        emitEvent: 'onCancelClicked',
      },
    ]"
    @onCancelClicked="onCancelClicked"
    @onSubmitClicked="onSubmitClicked"
    :isLoading="isLoading"
  >
    <v-container pa-0 grid-list-lg>
      <v-form v-model="isFormValid" ref="form">
        <v-layout wrap>
          <v-flex xs12>
            <ControlCheckbox
              v-model="isHistoricActivity"
              :label="`Add historic activity before ${_formatJavascriptDate({
                date: minEquipmentActivityDateTime,
                timeZoneName: 'long',
              })}?`"
              :disabled="isLoading"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="availableActivityTypes"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="typeId"
              clearable
              label="Activity Type*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlAutoComplete
              :items="enabledUserList"
              item-text="name"
              item-value="id"
              v-model="userIdPerformedBy"
              clearable
              label="Performed By*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 v-if="type && type.isStatusChange">
            <ControlAutoComplete
              :items="availableAssetStatuses"
              autocomplete
              item-text="name"
              item-value="id"
              v-model="statusId"
              clearable
              label="Equipment Status*"
              :disabled="!typeId || isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 v-if="type && type.isLocationChange">
            <ControlAutoComplete
              :items="enabledLocationList"
              :item-text="
                (location) => {
                  return (
                    '' +
                    location.name +
                    ' - ' +
                    _entityPropertyValueByName({
                      entityList: clientList,
                      idKey: 'id',
                      idValue: location.clientIdOwner,
                      propertyKey: 'name',
                    })
                  );
                }
              "
              item-value="id"
              v-model="locationId"
              clearable
              label="Location*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlDatePicker
              v-model="datePerformedOn"
              :min="minDatePerformedOnComputed"
              :max="maxDatePerformedOnComputed"
              label="Activity Date*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 md6>
            <ControlTimePicker
              v-model="timePerformedOn"
              :min="minTimePerformedOnComputed"
              :max="maxTimePerformedOnComputed"
              label="Activity Time*"
              :disabled="isLoading"
              :rules="[(v) => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlAutoComplete
              :items="selectedAssets"
              :item-text="
                (asset) => {
                  return (
                    '' +
                    asset.name +
                    ' - ' +
                    _entityPropertyValueByName({
                      entityList: modelList,
                      idKey: 'id',
                      idValue: asset.modelId,
                      propertyKey: 'name',
                    }) +
                    ' - ' +
                    _entityPropertyValueByName({
                      entityList: locationList,
                      idKey: 'id',
                      idValue: asset.locationId,
                      propertyKey: 'name',
                    })
                  );
                }
              "
              item-value="id"
              multiple
              chips
              deletable-chips
              v-model="assetIds"
              label="Equipment*"
              :disabled="isLoading"
              :rules="[(v) => (v && v.length > 0) || 'Required']"
            />
          </v-flex>
          <v-flex xs12>
            <ControlTextArea
              v-model="comments"
              label="Comments"
              :disabled="isLoading"
            />
          </v-flex>
          <v-flex xs12>
            <ControlFileInput
              v-model="files"
              label="Comments"
              :disabled="isLoading"
            />
          </v-flex>
        </v-layout>
        <small>*indicates required field</small>
      </v-form>
    </v-container>
  </DialogContainer>
</template>

<script>
import mixinAssetHelper from '../../../common/mixins/mixinAssetHelper'

import DialogContainer from '../../../common/components/layouts/DialogContainer'
import ControlTextField from '../../../common/components/controls/ControlTextField'
import ControlDatePicker from '../../../common/components/controls/ControlDatePicker'
import ControlTimePicker from '../../../common/components/controls/ControlTimePicker'
import ControlAutoComplete from '../../../common/components/controls/ControlAutoComplete'
import ControlTextArea from '../../../common/components/controls/ControlTextArea'
import ControlCheckbox from '../../../common/components/controls/ControlCheckbox'
import ControlFileInput from '../../../common/components/controls/ControlFileInput'

export default {
  components: {
    DialogContainer,
    ControlTextField,
    ControlDatePicker,
    ControlTimePicker,
    ControlAutoComplete,
    ControlTextArea,
    ControlCheckbox,
    ControlFileInput,
  },
  mixins: [mixinAssetHelper],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    selectedAssets: {
      type: Array,
      required: true,
    },
    modelList: {
      type: Array,
      required: true,
    },
    locationList: {
      type: Array,
      required: true,
    },
    userList: {
      type: Array,
      required: true,
    },
    activityTypeList: {
      type: Array,
      required: true,
    },
    assetStatusList: {
      type: Array,
      required: true,
    },
    clientList: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isFormValid: false,
    typeId: null,
    statusId: null,
    datePerformedOn: null,
    timePerformedOn: null,
    minEquipmentActivityDateTime: null,
    maxEquipmentActivityDateString: null,
    maxEquipmentActivityTimeString: null,
    minEquipmentActivityDateString: null,
    minEquipmentActivityTimeString: null,
    userIdPerformedBy: null,
    isHistoricActivity: null,
    assetIds: null,
    comments: null,
    locationId: null,
    files: null,
    availableActivityTypes: [],
    availableAssetStatuses: [],
  }),
  computed: {
    enabledUserList() {
      return this.userList.filter((user) => user.isEnabled)
    },
    enabledLocationList() {
      return this.locationList.filter((location) => location.isEnabled)
    },
    type() {
      return this.activityTypeList.find(
        (activityType) => activityType.id == this.typeId
      )
    },
    minDatePerformedOnComputed() {
      if (!this.isHistoricActivity) {
        return this.maxEquipmentActivityDateString
      }
      return null
    },
    minTimePerformedOnComputed() {
      if (
        new Date(this.datePerformedOn) <=
          new Date(this.minDatePerformedOnComputed) &&
        !this.isHistoricActivity
      ) {
        return this.maxEquipmentActivityTimeString
      }
      return null
    },
    maxDatePerformedOnComputed() {
      if (this.isHistoricActivity) {
        return this.minEquipmentActivityDateString
      }
      return null
    },
    maxTimePerformedOnComputed() {
      if (
        new Date(this.datePerformedOn) >=
          new Date(this.maxDatePerformedOnComputed) &&
        this.isHistoricActivity
      ) {
        return this.minEquipmentActivityTimeString
      }
      return null
    },
  },
  watch: {
    showDialog() {
      if (this.showDialog && this.selectedAssets) {
        this.assetIds = this.selectedAssets.map((asset) => asset.id)

        const maxEquipmentLocationUpdated = new Date(
          Math.max.apply(
            null,
            this.selectedAssets.map((x) => x.dateTimeLocationLastUpdated)
          )
        )
        const maxEquipmentStatusUpdated = new Date(
          Math.max.apply(
            null,
            this.selectedAssets.map((x) => x.dateTimeStatusLastUpdated)
          )
        )
        const maxEquipmentActivityDateTime =
          maxEquipmentLocationUpdated > maxEquipmentStatusUpdated
            ? maxEquipmentLocationUpdated
            : maxEquipmentStatusUpdated
        maxEquipmentActivityDateTime.setMinutes(
          maxEquipmentActivityDateTime.getMinutes() + 1
        )
        if (maxEquipmentActivityDateTime) {
          const { dateExtracted, timeExtracted } = this._getDateParts(
            maxEquipmentActivityDateTime
          )
          this.maxEquipmentActivityDateString = dateExtracted
          this.maxEquipmentActivityTimeString = timeExtracted
          this.datePerformedOn = this.maxEquipmentActivityDateString
          this.timePerformedOn = this.maxEquipmentActivityTimeString
        }

        const minEquipmentLocationUpdated = new Date(
          Math.min.apply(
            null,
            this.selectedAssets.map((x) => x.dateTimeLocationLastUpdated)
          )
        )
        const minEquipmentStatusUpdated = new Date(
          Math.min.apply(
            null,
            this.selectedAssets.map((x) => x.dateTimeStatusLastUpdated)
          )
        )
        const minEquipmentActivityDateTime =
          minEquipmentLocationUpdated < minEquipmentStatusUpdated
            ? minEquipmentLocationUpdated
            : minEquipmentStatusUpdated
        minEquipmentActivityDateTime.setMinutes(
          minEquipmentActivityDateTime.getMinutes() - 1
        )
        if (minEquipmentActivityDateTime) {
          this.minEquipmentActivityDateTime = minEquipmentActivityDateTime
          const { dateExtracted, timeExtracted } = this._getDateParts(
            minEquipmentActivityDateTime
          )
          this.minEquipmentActivityDateString = dateExtracted
          this.minEquipmentActivityTimeString = timeExtracted
        }

        this.setAvailableActivityTypes()
      }
      if (!this.showDialog) {
        this.clearDialog()
      }
    },
    typeId() {
      this.setAvailableAssetStatuses()
    },
    isHistoricActivity() {
      if (this.isHistoricActivity) {
        this.datePerformedOn = this.minEquipmentActivityDateString
        this.timePerformedOn = this.minEquipmentActivityTimeString
      } else {
        this.datePerformedOn = this.maxEquipmentActivityDateString
        this.timePerformedOn = this.maxEquipmentActivityTimeString
      }
      this.setAvailableActivityTypes()
    },
  },
  methods: {
    setAvailableActivityTypes() {
      const statusActivityTypeIdsList = this.assetStatusList
        .filter((assetStatus) =>
          this.selectedAssets
            .map((asset) => asset.statusId)
            .includes(assetStatus.id)
        )
        .map((assetStatus) => assetStatus.activityTypeIds)

      this.availableActivityTypes = this.activityTypeList.filter(
        (activityType) =>
          activityType.isEnabled &&
          (this.isHistoricActivity ||
            statusActivityTypeIdsList.every((statusActivityTypeIds) =>
              statusActivityTypeIds.includes(activityType.id)
            ))
      )
    },
    setAvailableAssetStatuses() {
      if (!this.typeId) {
        this.availableAssetStatuses = []
        return
      }
      const activityType = this.activityTypeList.find(
        (activityType) => activityType.id === this.typeId
      )
      this.availableAssetStatuses = this.assetStatusList.filter(
        (assetStatus) =>
          assetStatus.isEnabled &&
          activityType.assetStatusIds.includes(assetStatus.id)
      )
    },
    onSubmitClicked() {
      if (!this.assetIds.length) {
        this.assetIds = [this.assetIds]
      }
      this.$emit('onSubmitClicked', {
        typeId: this.typeId,
        statusId: this.statusId,
        dateTimeEpochPerformedOn: new Date(
          this.datePerformedOn + ' ' + this.timePerformedOn
        ).valueOf(),
        userIdPerformedBy: this.userIdPerformedBy,
        assetIds: this.assetIds,
        comments: this.comments,
        locationId: this.locationId,
        files: this.files,
      })
    },
    onCancelClicked() {
      this.$emit('onCancelClicked')
    },
    clearDialog() {
      this.$refs.form.reset()
      this.isHistoricActivity = null
      this.typeId = null
      this.statusId = null
      this.files = null
      this.datePerformedOn = null
      this.timePerformedOn = null
      this.minEquipmentActivityDate = null
      this.maxEquipmentActivityDate = null
      this.userIdPerformedBy = null
      this.assetIds = null
      this.comments = null
      this.locationId = null
    },
  },
}
</script>